import React, { useState, useEffect } from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LayoutGrid, Lock, Unlock, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DashboardWidget {
  id: string;
  title: string;
  component: React.ReactNode;
  defaultLayout: Layout;
}

interface DraggableDashboardProps {
  widgets: DashboardWidget[];
  onLayoutChange?: (layout: Layout[]) => void;
  savedLayout?: Layout[];
  isSpanish?: boolean;
}

export const DraggableDashboard: React.FC<DraggableDashboardProps> = ({
  widgets,
  onLayoutChange,
  savedLayout,
  isSpanish = false,
}) => {
  const [layout, setLayout] = useState<Layout[]>(
    savedLayout || widgets.map(w => w.defaultLayout)
  );
  const [isLocked, setIsLocked] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (savedLayout) {
      setLayout(savedLayout);
    }
  }, [savedLayout]);

  const handleLayoutChange = (newLayout: Layout[]) => {
    setLayout(newLayout);
    onLayoutChange?.(newLayout);
  };

  const handleResetLayout = () => {
    const defaultLayout = widgets.map(w => w.defaultLayout);
    setLayout(defaultLayout);
    onLayoutChange?.(defaultLayout);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">
            {isSpanish ? 'Panel Personalizable' : 'Customizable Dashboard'}
          </h3>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsLocked(!isLocked)}
          >
            {isLocked ? (
              <>
                <Lock className="w-4 h-4 mr-2" />
                {isSpanish ? 'Desbloquear' : 'Unlock'}
              </>
            ) : (
              <>
                <Unlock className="w-4 h-4 mr-2" />
                {isSpanish ? 'Bloquear' : 'Lock'}
              </>
            )}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetLayout}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {isSpanish ? 'Restablecer' : 'Reset'}
          </Button>
        </div>
      </div>

      {!isLocked && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-sm">
          {isSpanish 
            ? '💡 Arrastra los widgets para reorganizarlos. Arrastra desde las esquinas para redimensionar.'
            : '💡 Drag widgets to rearrange. Drag from corners to resize.'}
        </div>
      )}

      <GridLayout
        className={cn(
          "layout transition-all",
          isDragging && "dragging"
        )}
        layout={layout}
        cols={12}
        rowHeight={60}
        width={1200}
        isDraggable={!isLocked}
        isResizable={!isLocked}
        onLayoutChange={handleLayoutChange}
        onDragStart={() => setIsDragging(true)}
        onDragStop={() => setIsDragging(false)}
        draggableHandle=".drag-handle"
        containerPadding={[0, 0]}
        margin={[16, 16]}
      >
        {widgets.map((widget) => (
          <div
            key={widget.id}
            className={cn(
              "rounded-lg border bg-card shadow-sm transition-shadow",
              !isLocked && "hover:shadow-md cursor-move"
            )}
          >
            <Card className="h-full border-0">
              <CardHeader className={cn(
                "pb-3",
                !isLocked && "drag-handle cursor-grab active:cursor-grabbing"
              )}>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{widget.title}</span>
                  {!isLocked && (
                    <LayoutGrid className="w-4 h-4 text-muted-foreground" />
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-auto h-[calc(100%-4rem)]">
                {widget.component}
              </CardContent>
            </Card>
          </div>
        ))}
      </GridLayout>

      <style dangerouslySetInnerHTML={{ __html: `
        .react-grid-item.react-draggable-dragging {
          z-index: 100;
          opacity: 0.9;
        }
        
        .react-grid-item > .react-resizable-handle {
          opacity: 0;
          transition: opacity 0.2s;
        }
        
        .react-grid-item:hover > .react-resizable-handle {
          opacity: 1;
        }

        .react-grid-item > .react-resizable-handle::after {
          border-right: 2px solid hsl(var(--primary));
          border-bottom: 2px solid hsl(var(--primary));
        }

        .layout.dragging .react-grid-item {
          transition: none;
        }
      ` }} />
    </div>
  );
};
